import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useRouter } from '@tanstack/react-router';
import { getHistoryPosition, useHistoryPosition } from './historyPosition.js';
import { runHistoryArrow, stepPastBlockers, type HistoryArrowDirection } from './historyArrows.js';
import { pendingNavigationGuard } from './navigationGuard.js';
import { requestWorkspaceNavigation } from './workspaceNavigation.js';

/**
 * The top bar's Back / Forward arrows. They walk the same history as the
 * browser's own buttons, the mouse's side buttons and a trackpad swipe, and
 * never leave the dashboard: each is off when there is no entry of this
 * dashboard visit in that direction (a fresh tab, after sign-in, or when the
 * position cannot be told). Hidden on phones, which have their own back
 * gesture. Statically imported by TopBar, which is in the always-loaded
 * bundle, so it stays small.
 *
 * "Off" is aria-disabled plus a click guard, not the disabled attribute: a
 * disabled button drops keyboard focus to the page, which would happen to
 * someone pressing Enter on Back until the first entry.
 */
export function HistoryNav() {
  const router = useRouter();
  const { canGoBack, canGoForward } = useHistoryPosition();

  function go(direction: HistoryArrowDirection): void {
    runHistoryArrow(direction, {
      canMove: (dir) => {
        const now = getHistoryPosition();
        return dir === 'back' ? now.canGoBack : now.canGoForward;
      },
      requestMove: requestWorkspaceNavigation,
      pendingGuard: pendingNavigationGuard,
      confirm: (message) => window.confirm(message),
      stepPastBlockers: (dir) => stepPastBlockers(router.history, dir),
      // The browser's own step: popstate runs every page's blocker, as for
      // the browser's buttons. (router.history.back() would also suppress the
      // next "leave this page?" prompt on reload or close, even long after.)
      step: (dir) => {
        if (dir === 'back') window.history.back();
        else window.history.forward();
      },
    });
  }

  return (
    <div className="topbar-history-nav hide-mobile" role="group" aria-label="Page history">
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        onClick={() => go('back')}
        aria-disabled={canGoBack ? undefined : true}
        aria-label="Back"
        title="Back"
      >
        <ArrowLeft />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        onClick={() => go('forward')}
        aria-disabled={canGoForward ? undefined : true}
        aria-label="Forward"
        title="Forward"
      >
        <ArrowRight />
      </button>
    </div>
  );
}
